import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useCreators } from "@/context/CreatorsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Plus, 
  Search, 
  Pencil, 
  Trash2, 
  Eye,
  EyeOff,
  ExternalLink,
  MoreVertical,
  Star,
  StarOff,
  ImageOff,
  Image,
  Heart,
  Film
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState, useEffect } from "react";
import { toast } from "sonner";

const AdminCreators = () => {
  const { creators, deleteCreator, restoreCreator, toggleCreatorStatus, toggleCreatorFeatured, toggleCreatorBranding, toggleCreatorShorts, fetchAllCreators, isLoading } = useCreators();
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [creatorToDelete, setCreatorToDelete] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active"); // 'active' | 'inactive' | 'deleted' | 'all'

  // Fetch all creators (including inactive) on mount
  useEffect(() => {
    fetchAllCreators();
  }, [fetchAllCreators]);

  const filteredCreators = creators.filter(creator => {
    const query = searchQuery.toLowerCase();
    const matchesQuery = (
      creator.name.toLowerCase().includes(query) ||
      creator.username.toLowerCase().includes(query)
    );
    if (!matchesQuery) return false;
    // Status filter
    if (statusFilter === "active") return creator.active && !creator.deleted;
    if (statusFilter === "inactive") return !creator.active && !creator.deleted;
    if (statusFilter === "deleted") return creator.deleted === true;
    return true; // 'all'
  });

  const counts = {
    active: creators.filter(c => c.active && !c.deleted).length,
    inactive: creators.filter(c => !c.active && !c.deleted).length,
    deleted: creators.filter(c => c.deleted).length,
    all: creators.length,
  };

  const handleRestore = async (creator) => {
    try {
      await restoreCreator(creator.id);
      toast.success(`@${creator.username} restaurada (queda inactiva hasta que la actives)`);
    } catch {
      toast.error("Error al restaurar");
    }
  };

  const handleDelete = (creator) => {
    setCreatorToDelete(creator);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (creatorToDelete) {
      try {
        await deleteCreator(creatorToDelete.id);
        toast.success("Creadora eliminada");
      } catch (err) {
        toast.error("Error al eliminar la creadora");
      }
    }
    setDeleteDialogOpen(false);
    setCreatorToDelete(null);
  };

  const handleToggleStatus = async (creator) => {
    try {
      await toggleCreatorStatus(creator.id);
      toast.success(creator.active ? "Creadora desactivada" : "Creadora activada");
    } catch (err) {
      toast.error("Error al cambiar el estado");
    }
  };

  const handleToggleFeatured = async (creator) => {
    try {
      await toggleCreatorFeatured(creator.id);
      toast.success(creator.featured ? "Quitada de destacados" : "Agregada a destacados");
    } catch (err) {
      toast.error("Error al cambiar destacado");
    }
  };

  const handleToggleBranding = async (creator) => {
    try {
      await toggleCreatorBranding(creator.id);
      toast.success(creator.hideBranding ? "Logo visible" : "Logo oculto");
    } catch (err) {
      toast.error("Error al cambiar branding");
    }
  };

  const handleToggleShorts = async (creator) => {
    try {
      await toggleCreatorShorts(creator.id);
      toast.success(creator.shortsEnabled ? "Shorts deshabilitados" : "Shorts habilitados");
    } catch (err) {
      toast.error("Error al cambiar shorts");
    }
  };

  return (
    <AdminLayout 
      title="Creadoras" 
      subtitle={`${creators.length} creadoras registradas`}
      actions={
        <Link to="/admin/creadoras/nueva">
          <Button className="rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 border-0">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Creadora
          </Button>
        </Link>
      }
    >
      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            type="text"
            placeholder="Buscar por nombre o username..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-11 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-pink-500/50"
          />
        </div>
      </div>

      {/* Status filter tabs */}
      <div className="mb-6 flex flex-wrap gap-2" data-testid="creators-status-tabs">
        {[
          { key: "active", label: "Activas", color: "green" },
          { key: "inactive", label: "Inactivas", color: "amber" },
          { key: "deleted", label: "Eliminadas", color: "red" },
          { key: "all", label: "Todas", color: "white" },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setStatusFilter(t.key)}
            data-testid={`filter-tab-${t.key}`}
            className={`px-4 py-1.5 rounded-full text-xs font-medium border transition inline-flex items-center gap-2 ${
              statusFilter === t.key
                ? "bg-pink-500/20 border-pink-400/50 text-pink-100"
                : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10"
            }`}
          >
            {t.label}
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
              t.color === "green" ? "bg-green-500/25 text-green-200" :
              t.color === "amber" ? "bg-amber-500/25 text-amber-200" :
              t.color === "red" ? "bg-red-500/25 text-red-200" :
              "bg-white/15 text-white"
            }`}>
              {counts[t.key]}
            </span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
        {filteredCreators.length > 0 ? (
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left text-xs font-medium text-white/50 uppercase tracking-wider py-4 px-6">Creadora</th>
                <th className="text-left text-xs font-medium text-white/50 uppercase tracking-wider py-4 px-6">Username</th>
                <th className="text-left text-xs font-medium text-white/50 uppercase tracking-wider py-4 px-6">Enlaces</th>
                <th className="text-left text-xs font-medium text-white/50 uppercase tracking-wider py-4 px-6">Estado</th>
                <th className="text-left text-xs font-medium text-white/50 uppercase tracking-wider py-4 px-6">Destacado</th>
                <th className="text-left text-xs font-medium text-white/50 uppercase tracking-wider py-4 px-6">Branding</th>
                <th className="text-left text-xs font-medium text-white/50 uppercase tracking-wider py-4 px-6">Shorts</th>
                <th className="text-right text-xs font-medium text-white/50 uppercase tracking-wider py-4 px-6">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredCreators.map((creator) => (
                <tr key={creator.id} className="border-t border-white/5 hover:bg-white/5 transition-colors">
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-3">
                      <img
                        src={creator.avatar}
                        alt={creator.name}
                        className="w-10 h-10 rounded-full object-cover ring-2 ring-white/10"
                      />
                      <div>
                        <p className="font-medium text-white">{creator.name}</p>
                        <p className="text-xs text-white/40 truncate max-w-[200px]">
                          {creator.bio}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className="text-sm text-white/60">@{creator.username}</span>
                  </td>
                  <td className="py-4 px-6">
                    <span className="text-sm text-white">{creator.links?.length || 0}</span>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                      creator.active 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-white/10 text-white/50'
                    }`}>
                      {creator.active ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <button 
                      onClick={() => handleToggleFeatured(creator)}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        creator.featured 
                          ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30' 
                          : 'bg-white/10 text-white/50 hover:bg-white/20'
                      }`}
                    >
                      <Star className={`w-3 h-3 ${creator.featured ? 'fill-yellow-400' : ''}`} />
                      {creator.featured ? 'Sí' : 'No'}
                    </button>
                  </td>
                  <td className="py-4 px-6">
                    <button 
                      onClick={() => handleToggleBranding(creator)}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        creator.hideBranding 
                          ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30' 
                          : 'bg-white/10 text-white/50 hover:bg-white/20'
                      }`}
                      title={creator.hideBranding ? "Logo oculto" : "Logo visible"}
                    >
                      {creator.hideBranding ? <ImageOff className="w-3 h-3" /> : <Image className="w-3 h-3" />}
                      {creator.hideBranding ? 'Oculto' : 'Visible'}
                    </button>
                  </td>
                  <td className="py-4 px-6">
                    <button
                      onClick={() => handleToggleShorts(creator)}
                      data-testid={`toggle-shorts-${creator.username}`}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        creator.shortsEnabled
                          ? 'bg-pink-500/20 text-pink-300 hover:bg-pink-500/30'
                          : 'bg-white/10 text-white/50 hover:bg-white/20'
                      }`}
                      title={creator.shortsEnabled ? "Shorts visibles en /shorts" : "Sin shorts en /shorts"}
                    >
                      <Film className="w-3 h-3" />
                      {creator.shortsEnabled ? 'Activo' : 'No'}
                    </button>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center justify-end gap-1">
                      <a
                        href={`/${creator.username}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Ver perfil público (como lo ven los fans)"
                        data-testid={`view-profile-${creator.username}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white text-xs font-medium transition-colors"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Ver</span>
                      </a>
                      {creator.jgfansEnabled && (
                        <button
                          onClick={async () => {
                            try {
                              const r = await fetch(`/api/jgfans/${creator.username}/login`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ email: "jgmodels", password: "admin" }),
                              });
                              if (!r.ok) { alert("No se pudo entrar como auditor"); return; }
                              const d = await r.json();
                              localStorage.setItem(`jgfans_${creator.username}`, d.token);
                              window.open(`/jgfans/${creator.username}/feed`, "_blank");
                            } catch { alert("Error de red"); }
                          }}
                          title="Ver feed JGFans como auditor"
                          data-testid={`view-jgfans-${creator.username}`}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-purple-500/15 hover:bg-purple-500/25 border border-purple-500/30 text-purple-200 text-xs font-medium transition-colors"
                        >
                          <Heart className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">JGFans</span>
                        </button>
                      )}
                      <Link
                        to={`/admin/creadoras/editar/${creator.id}`}
                        title="Configurar perfil"
                        data-testid={`configure-${creator.username}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-pink-500/15 hover:bg-pink-500/25 border border-pink-500/30 text-pink-200 text-xs font-medium transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Configurar</span>
                      </Link>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-2 rounded-lg hover:bg-white/10 transition-colors text-white/40 hover:text-white">
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48 bg-[#1a1a1a] border-white/10">
                          <DropdownMenuItem asChild>
                            <Link to={`/admin/creadoras/editar/${creator.id}`} className="cursor-pointer text-white/80 hover:text-white focus:text-white">
                              <Pencil className="w-4 h-4 mr-2" />
                              Editar
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleStatus(creator)} className="cursor-pointer text-white/80 hover:text-white focus:text-white">
                            {creator.active ? (
                              <>
                                <EyeOff className="w-4 h-4 mr-2" />
                                Desactivar
                              </>
                            ) : (
                              <>
                                <Eye className="w-4 h-4 mr-2" />
                                Activar
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleFeatured(creator)} className="cursor-pointer text-white/80 hover:text-white focus:text-white">
                            {creator.featured ? (
                              <>
                                <StarOff className="w-4 h-4 mr-2" />
                                Quitar de destacados
                              </>
                            ) : (
                              <>
                                <Star className="w-4 h-4 mr-2" />
                                Destacar en landing
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleBranding(creator)} className="cursor-pointer text-white/80 hover:text-white focus:text-white">
                            {creator.hideBranding ? (
                              <>
                                <Image className="w-4 h-4 mr-2" />
                                Mostrar logo
                              </>
                            ) : (
                              <>
                                <ImageOff className="w-4 h-4 mr-2" />
                                Ocultar logo
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-white/10" />
                          {creator.deleted ? (
                            <DropdownMenuItem
                              onClick={() => handleRestore(creator)}
                              data-testid={`restore-${creator.username}`}
                              className="cursor-pointer text-emerald-300 focus:text-emerald-300"
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              Restaurar
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => handleDelete(creator)}
                              className="cursor-pointer text-red-400 focus:text-red-400"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-12">
            <p className="text-white/50 mb-4">
              {searchQuery ? "No se encontraron resultados" : "No hay creadoras registradas"}
            </p>
            {!searchQuery && (
              <Link to="/admin/creadoras/nueva">
                <Button className="rounded-xl bg-gradient-to-r from-slate-600 to-blue-600 hover:from-slate-700 hover:to-blue-700 border-0">
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar creadora
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#1a1a1a] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">¿Eliminar creadora?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              Estás a punto de eliminar a <strong className="text-white">{creatorToDelete?.name}</strong>. 
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white border-0">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminCreators;
